import { useState } from "react";
import {
  MessageSquarePlus,
  FileText,
  Send,
  PanelLeftClose,
  Info,
  Sparkles,
} from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { FEATURE_KEYS } from "@/lib/featureCatalog";
import { useI18n } from "@/lib/i18n";

interface SupplierChatSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  supplierName: string;
  onNewChat: () => void;
  onInquiry: () => void;
  onMessage: () => void;
  onAbout: () => void;
  /** Public feature entitlements for the supplier (from get_supplier_public_features) */
  publicFeatures?: Record<string, string>;
}

export function SupplierChatSidebar({
  isOpen,
  onToggle,
  supplierName,
  onNewChat,
  onInquiry,
  onMessage,
  onAbout,
  publicFeatures = {},
}: SupplierChatSidebarProps) {
  const { t } = useI18n();
  const isFreeMode = publicFeatures[FEATURE_KEYS.CHATBOT_PROMO] !== "allowed";

  const handleAction = (action: () => void) => {
    action();
    if (window.innerWidth < 1024) onToggle();
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-30 lg:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        className={`fixed top-0 left-0 bottom-0 z-40 bg-sidebar flex flex-col transition-transform duration-200 ease-in-out w-[260px] border-r border-sidebar-border
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Top: Supplier name + close */}
        <div className="px-3 h-14 flex items-center gap-2 border-b border-sidebar-border">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-sidebar-foreground truncate">
              {supplierName}
            </p>
          </div>
          <button
            onClick={onToggle}
            className="p-2 rounded-xl text-muted-foreground hover:bg-sidebar-accent transition-colors flex-shrink-0"
          >
            <PanelLeftClose className="w-5 h-5" />
          </button>
        </div>

        {/* Primary actions */}
        <nav className="flex-1 overflow-y-auto px-3 pt-3">
          <div className="space-y-1">
            {/* Nový chat */}
            <button
              onClick={() => handleAction(onNewChat)}
              className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors w-full text-left"
            >
              <MessageSquarePlus className="w-5 h-5 flex-shrink-0" />
              <span>{t("supplierChat.newChat")}</span>
            </button>

            {/* Poptávka */}
            <button
              onClick={() => handleAction(onInquiry)}
              className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors w-full text-left"
            >
              <FileText className="w-5 h-5 flex-shrink-0" />
              <span>{t("supplierChat.inquiry")}</span>
            </button>

            {/* Zpráva */}
            <button
              onClick={() => handleAction(onMessage)}
              className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors w-full text-left"
            >
              <Send className="w-5 h-5 flex-shrink-0" />
              <span>{t("supplierChat.message")}</span>
            </button>
          </div>
        </nav>

        {/* Free-mode upsell promo block */}
        {isFreeMode && (
          <div className="px-3 pb-2 pt-1 border-t border-sidebar-border">
            <div className="px-3 py-3 rounded-xl bg-primary/5 border border-primary/10">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-foreground">
                    {t("supplierChat.upsellTitle")}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                    {t("supplierChat.upsellDesc")}
                  </p>
                  <a
                    href="/login"
                    className="inline-block mt-2 text-[11px] font-medium text-primary hover:underline"
                  >
                    {t("supplierChat.upsellLink")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer: Language + O nás */}
        <div className="px-3 pb-3 pt-2 flex items-center justify-between border-t border-sidebar-border">
          <LanguageSwitcher dropUp />
          <button
            onClick={() => handleAction(onAbout)}
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs text-muted-foreground hover:bg-muted/70 hover:text-foreground transition-colors"
          >
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{t("supplierChat.aboutUs")}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
